export enum Role {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  DISPATCHER = 'dispatcher',
  DRIVER = 'driver',
  LAND_TRANSPORTER = 'land_transporter',
  MANAGER = 'manager',
  READ_ONLY = 'read_only',
}

// FL-132 デモデータ関連の型定義をエクスポート
export * from './types/demo';

// FL-163 スケジュール関連の型定義をエクスポート
export * from './types/schedule';

// FL-118 日次レポート機能の型定義
export type DailyReportType = 'case_list' | 'driver_schedule' | 'combined';
export type DailyReportStatus = 'success' | 'failed' | 'partial';
export type ReportFileFormat = 'csv' | 'pdf';

export interface DailyReportSettings {
  id: string;
  companyId: string;
  reportType: DailyReportType;
  sendTime: string; // HH:MM format
  sendToEmails: string[];
  includeCsv: boolean;
  includePdf: boolean;
  dateRangeDays: number;
  isActive: boolean;
  emailSubjectTemplate?: string;
  emailBodyTemplate?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface DailyReportLog {
  id: string;
  companyId: string;
  reportDate: string; // YYYY-MM-DD format
  reportType: DailyReportType;
  recipients: string[];
  fileFormats: ReportFileFormat[];
  status: DailyReportStatus;
  errorMessage?: string;
  fileSizes?: Record<ReportFileFormat, number>;
  executionTimeMs?: number;
  retryCount: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  csvFileSizeBytes?: number;
  pdfFileSizeBytes?: number;
  casesCount: number;
  driversCount: number;
  sentAt: string;
  createdBy?: string;
}

export interface EmailAttachment {
  filename: string;
  content: Buffer;
  contentType: string;
  size: number;
}

export interface DailyReportData {
  companyInfo: {
    id: string;
    name: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
  };
  reportDate: string;
  dateRange: {
    start: string;
    end: string;
  };
  cases: Case[];
  drivers: LandTransporter[];
  summary: {
    totalCases: number;
    totalDrivers: number;
    completedCases: number;
    pendingCases: number;
  };
}

export interface EmailTemplate {
  subject: string;
  body: string;
  variables: Record<string, string>;
}

export interface User {
  id: string;
  email?: string;
  name: string;
  role: Role;
  company: string;
  companyId?: string; // 陸送会社ID（super_adminの場合はnull）
  landTransporterId?: string; // 陸送員の場合のみ設定
}

export interface WorkHours {
  startTime: string;
  endTime: string;
}

export interface WeeklySchedule {
  月: WorkHours | null;
  火: WorkHours | null;
  水: WorkHours | null;
  木: WorkHours | null;
  金: WorkHours | null;
  土: WorkHours | null;
  日: WorkHours | null;
}

export interface Manufacturer {
  id: string;
  name: string; // e.g., 'トヨタ', '日産'
  country: string; // e.g., '日本', 'ドイツ'
}

// 車両パワートレイン種別
export enum VehiclePowertrain {
  GASOLINE = 'gasoline', // ガソリン
  DIESEL = 'diesel', // ディーゼル
  HYBRID = 'hybrid', // ハイブリッド
  SERIES_HYBRID = 'series_hybrid', // シリーズHV
  MILD_HYBRID = 'mild_hybrid', // マイルドHV
  PLUGIN_HYBRID = 'plugin_hybrid', // プラグインHV
  ELECTRIC = 'electric', // 電気
}

// 車両タグ種別
export enum VehicleTag {
  IMMOBILIZED = 'immobilized', // 不動車
  LOW_HEIGHT = 'low_height', // 低車高
  WIDE_BODY = 'wide_body', // ワイドボディ
  WELFARE = 'welfare', // 福祉車両
  CAMPING = 'camping', // キャンピングカー
  CLASSIC = 'classic', // クラシックカー
}

// 車両モデルデータソース種別
export enum VehicleModelDataSource {
  RAKUHAI_PROVIDED = 'rakuhai_provided', // ラクハイ提供データ
  CUSTOM = 'custom', // 陸送会社独自データ
}

export interface VehicleModel {
  id: string;
  manufacturerId: string; // Foreign key to Manufacturer
  name: string; // e.g., 'プリウス', 'クラウン' (メーカー名を除く)
  vehicleTypeId: string; // Foreign key to VehicleType
  // 新規フィールド（任意）
  powertrain?: VehiclePowertrain; // パワートレイン（任意選択）
  vehicleTags?: VehicleTag[]; // 車両タグ（複数選択可・任意）
  // 削除制御用フィールド
  dataSource: VehicleModelDataSource; // データソース（ラクハイ提供/独自）
}

export interface VehicleType {
  id: string;
  name: string;
}

export interface WorkingTimeSlot {
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

// FL-59: 時間ピッカーのデフォルト選択時間設定
export interface TimePickerSettings {
  minTime: string; // HH:mm format - 選択可能な最早時間
  maxTime: string; // HH:mm format - 選択可能な最遅時間
  excludedHours: string[]; // HH:mm format - 除外する時間（深夜・早朝等）
  minimumDuration: number; // 引き取り→搬入の最小間隔（分）
}

// FL-81: 昼休み時間設定
export interface LunchTimeSettings {
  startTime: string; // HH:mm format - 昼休み開始時間
  endTime: string; // HH:mm format - 昼休み終了時間
  bufferMinutes: number; // 昼休み前後のバッファ時間（分）
}

// FL-81: 距離別移動時間設定
export interface DistanceBufferSettings {
  local: number; // 市内移動バッファ時間（分）
  prefectural: number; // 県内移動バッファ時間（分）
  crossPrefecture: number; // 県外移動バッファ時間（分）
}

// FL-81: バッファ時間設定
export interface BufferTimeSettings {
  betweenCasesBuffer: number; // 案件間移動バッファ（分）
  lunchTimeBuffer: LunchTimeSettings; // 昼休み時間設定
  longDistanceBuffer: DistanceBufferSettings; // 距離別移動時間設定
  dailyPreparationBuffer: number; // 1日最初の準備時間（分）
}

// FL-59: 陸送会社ごとの時間制限設定
export interface CompanyTimeSettings {
  id: string;
  companyId: string;
  pickupTimeSettings: TimePickerSettings;
  deliveryTimeSettings: TimePickerSettings;
  bufferTimeSettings: BufferTimeSettings; // FL-81: バッファ時間設定を追加
  createdAt: string;
  updatedAt: string;
}

// OCR機能設定
export interface CompanyOCRSettings {
  id: string;
  companyId: string;
  ocrEnabled: boolean; // OCR機能の有効/無効
  autoAddressExtraction: boolean; // 住所の自動抽出
  autoPhoneExtraction: boolean; // 電話番号の自動抽出
  confidenceThreshold: number; // 抽出の信頼度閾値 (0.0-1.0)
  maxFilesPerUpload: number; // 一度にアップロード可能な最大ファイル数
  maxFileSizeMB: number; // 最大ファイルサイズ（MB）
  createdAt: string;
  updatedAt: string;
}

// 締切ルール設定
export interface CompanyDeadlineRuleSettings {
  id: string;
  companyId: string;
  deadlineRulesEnabled: boolean; // 締切ルール機能の有効/無効
  // 追加設定
  bufferMinutes: number; // 自動適用時のデフォルトバッファ（分）
  autoApplyRules: boolean; // 取引先選択/日付変更時に自動適用するか
  warningThreshold: number; // 締切警告を出す閾値（分）
  createdAt: string;
  updatedAt: string;
}

// デフォルト締切ルール設定
export const DEFAULT_COMPANY_DEADLINE_RULE_SETTINGS: Omit<
  CompanyDeadlineRuleSettings,
  'id' | 'companyId' | 'createdAt' | 'updatedAt'
> = {
  // デフォルトで有効（システム設定で明示的に無効化可能）
  deadlineRulesEnabled: true,
  // デフォルトのバッファ・自動適用・警告閾値
  bufferMinutes: 30,
  autoApplyRules: true,
  warningThreshold: 60,
};

// FL-229: ドライバー表示設定機能
// ドライバーに陸送料金を表示するかどうかを制御する設定
export interface DriverTransportFeeVisibilitySettings {
  showTransportFeeToDriver: boolean; // ドライバーに陸送料金を表示するか
}

// デフォルト値（セキュリティ重視で false）
export const DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS: DriverTransportFeeVisibilitySettings =
  {
    showTransportFeeToDriver: false,
  };

// デフォルトOCR設定
export const DEFAULT_COMPANY_OCR_SETTINGS: Omit<
  CompanyOCRSettings,
  'id' | 'companyId' | 'createdAt' | 'updatedAt'
> = {
  ocrEnabled: true,
  autoAddressExtraction: true,
  autoPhoneExtraction: true,
  confidenceThreshold: 0.3,
  maxFilesPerUpload: 3,
  maxFileSizeMB: 10,
};

export interface LandTransporter {
  id: string;
  name: string;
  phone: string;
  address: string;
  status: 'Available' | 'Busy' | 'Unavailable';
  drivableVehicleTypes: string[]; // Array of vehicle type IDs
  // FL-563: workingDaysは段階的に削除予定（デフォルト値で初期化）
  // マイスケジュール機能（driver_schedules）への移行完了までの中間状態
  // TODO: 3ヶ月後に完全削除予定
  workingDays: {
    monday: WorkingTimeSlot | null;
    tuesday: WorkingTimeSlot | null;
    wednesday: WorkingTimeSlot | null;
    thursday: WorkingTimeSlot | null;
    friday: WorkingTimeSlot | null;
    saturday: WorkingTimeSlot | null;
    sunday: WorkingTimeSlot | null;
  };
  hasLicenseForLargeVehicles: boolean;
  canWorkNightShift: boolean;
  canWorkLongDistance: boolean;
  hasTemporaryLicense: boolean; // 仮ナンバーの有無
  isATOnly: boolean; // AT限定
  notes?: string; // 特記事項
  // FL-180: 業務制約フラグ
  isSolicitationProhibited: boolean; // 客引きNG
  isDesignatedStoreProhibited: boolean; // 指定店舗NG
  // FL-205: マッチング条件
  can_drive_left_hand?: boolean; // 左ハンドル車両運転可能フラグ（デフォルト: false）
  can_handle_no_inspection?: boolean; // 車検なし車両対応可能フラグ（デフォルト: true）
  can_drive_manual?: boolean; // MT車運転可能フラグ（デフォルト: true）
  // FL-270: 最寄駅情報（Google Maps統合）
  nearestStation?: string | null; // 最寄駅名 (例: 新宿駅)
  nearestStationPlaceId?: string | null; // Google Maps Place ID
  nearestStationLat?: number | null; // 最寄駅の緯度
  nearestStationLng?: number | null; // 最寄駅の経度
}

// ========================================
// FL-114: 陸送員の出勤日の公開範囲設定機能関連の型定義
// ========================================

// 陸送員出勤日の公開範囲
export enum DriverScheduleVisibility {
  ADMIN_ONLY = 'admin_only', // 管理者のみ
  ALL_DRIVERS = 'all_drivers', // 全陸送員に公開
}

// FL-115: 陸送員のスケジュール編集権限設定機能関連の型定義
export enum DriverScheduleEditPermission {
  ADMIN_ONLY = 'admin_only', // 管理者のみ編集可能
  DRIVER_SELF_EDIT = 'driver_self_edit', // 陸送員も自分のスケジュール編集可能
}

// 陸送員スケジュール
export interface DriverSchedule {
  id: string;
  companyId: string;
  driverId: string;
  scheduleDate: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
  endTime?: string; // HH:mm
  isAvailable: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

// 陸送員スケジュールの作成・更新用データ
export interface DriverScheduleInput {
  companyId: string;
  driverId: string;
  scheduleDate: string;
  startTime?: string;
  endTime?: string;
  isAvailable: boolean;
  notes?: string;
}

// 陸送員スケジュール一覧表示用（陸送員情報を含む）
export interface DriverScheduleWithDriver extends DriverSchedule {
  driver: {
    id: string;
    name: string;
    employeeCode?: string;
  };
}

// DEPRECATED: BusinessPartner統合済み - ClientCompatを使用
export type Client = ClientCompat;

export interface ReportItem {
  id: string; // e.g., 'front', 'right_side'
  name: string; // e.g., '正面', '右側面'
  photos: string[]; // Array of base64 encoded strings
  notes: string;
}

export interface VehicleConditionReport {
  items: ReportItem[];
}

export interface Case {
  id: string;
  companyId?: string; // 陸送会社ID（マルチテナント対応）
  caseNumber?: string; // システム生成の案件番号
  status:
    | 'provisional' // 🆕 仮受（電話・LINE相談段階、車体番号未確定）
    | 'confirmed' // 🆕 本受・未配車（正式依頼書受領後）
    | 'assigned' // 配車済み
    | 'picked_up' // 🆕 引き取り済み
    | 'in_transit' // 輸送中
    | 'delivered' // 🆕 搬入済み
    | 'reported' // 🆕 報告済み（作業完了報告提出済み）
    | 'completed'; // 完了
  acceptanceStatus?: 'provisional' | 'confirmed'; // 受付状態（仮受・本受）- 従来システム互換性のため
  assignedLandTransporterId?: string | null;

  // BusinessPartner IDと拠点指定（FL-28）
  // FL-89: 自由入力モード対応 - partner IDは省略可能（customフィールドに住所を格納）
  billingPartnerId?: string;
  billingLocationId?: string; // 請求先の特定拠点ID（未指定の場合はデフォルト拠点）

  pickupPartnerId?: string;
  pickupLocationId?: string; // 引き取り先の特定拠点ID（未指定の場合はデフォルト拠点）

  deliveryPartnerId?: string;
  deliveryLocationId?: string; // 搬入先の特定拠点ID（未指定の場合はデフォルト拠点）

  // Legacy Client IDs (後方互換性のため維持、将来的に廃止予定)
  billingClientId: string;
  pickupClientId: string;
  deliveryClientId: string;

  // FL-89: 請求先・引取先・搬入先の自由入力対応
  billingAddressCustom?: string; // 請求先住所（自由入力）
  pickupAddressCustom?: string; // 引取先住所（自由入力）
  deliveryAddressCustom?: string; // 搬入先住所（自由入力）

  // Pickup details
  pickupAddress: string;
  pickupPhone: string;
  pickupDate: string; // YYYY-MM-DD
  pickupTimeStart: string; // HH:mm
  pickupTimeEnd: string; // HH:mm
  // FL-89: 引取日期間選択対応
  pickupDateEnd?: string; // YYYY-MM-DD - 引取終了日（期間選択時）
  pickupReport: VehicleConditionReport | null;

  // Delivery details
  deliveryAddress: string;
  deliveryPhone: string;
  deliveryDate: string; // YYYY-MM-DD
  deliveryTime: string; // HH:mm
  // FL-89: 搬入日期間選択対応
  deliveryDateEnd?: string; // YYYY-MM-DD - 搬入終了日（期間選択時）
  deliveryReport: VehicleConditionReport | null;

  // Schedule details
  scheduledTime?: string; // HH:mm - スケジュール表示用時間（ドラッグ&ドロップ位置から計算）

  // Vehicle details
  vehicleModel: string; // 車両モデル選択（必須）- データベースのvehicle_modelフィールドと対応（レガシー・文字列）
  vehicleModelId?: string; // 車両モデルID（UUID）- データベースのvehicle_model_idフィールドと対応（新方式）
  vehicle_id?: string; // 車両ID - 実際のDBフィールド
  vehicleDisplayName?: string; // 表示用車両名（"トヨタ プリウス"など、company_casesテーブルから取得）
  vehicleType: string; // 車種（車両モデルから自動設定）
  vehiclePlateNumber: string; // ナンバープレート番号（手入力）
  // FL-89: ナンバープレート番号「なし」選択対応
  vehiclePlateNumberNone?: boolean; // ナンバープレート番号なしフラグ
  vehicleNumber?: string; // 車体番号（車台番号、VIN: Vehicle Identification Number）
  chassisNumber?: string | null; // FL-191: 車体番号（陸送員スケジュール画面表示用、データベースNULL許容）

  // FL-89: 新規フィールド追加
  requester?: string; // 依頼者（担当者）
  managementNumber?: string; // 管理番号
  transportFee?: number; // 陸送費

  // FL-113: 車検状態管理
  isVehicleInspectionExpired?: boolean; // 車検切れフラグ
  requiresTemporaryNumber?: boolean; // 仮ナンバー要否
  temporaryNumberNotes?: string; // 仮ナンバー関連備考

  // FL-205: マッチング条件
  transmission_type?: 'manual' | 'automatic'; // 変速機種別（MT: manual, AT: automatic）
  steering_position?: 'left' | 'right'; // ハンドル位置（left: 左ハンドル, right: 右ハンドル）
  has_vehicle_inspection?: boolean; // 車検あり・なし（true: 車検あり, false: 車検なし）

  // Billing
  price: number;
  billableDistance?: number; // 請求対象走行距離（km）- 稼働実績レポート計算用

  // Other
  remarks: string;
}

export interface CaseStatusHistory {
  id: string;
  caseId: string;
  previousStatus: string | null;
  newStatus: string;
  changedBy: string | null;
  notes: string | null;
  createdAt: string;
}

/**
 * スケジュール画面で使用される拡張案件型
 * FL-188: 車体番号・都道府県・ナンバープレート表示を含む
 *
 * @remarks
 * この型は、`useDriverSchedule`フックなどで案件データを加工した後の型を表します。
 * - `pickupPrefecture`: 引取先住所から抽出した都道府県名
 * - `vehiclePlateNumber`: ナンバープレート番号（表示用）
 */
export interface EnrichedCase extends Case {
  pickupPrefecture: string; // 引取先の都道府県（addressUtilsで抽出）
  vehiclePlateNumber: string; // ナンバープレート番号（Case.vehiclePlateNumberと同値）
}

export interface SuggestedTransporterInfo {
  transporterId: string;
  reason: string;
}

export interface DispatchPlan {
  landTransporterId: string;
  landTransporterName: string;
  caseIds: string[];
  reason?: string; // AI選択理由の説明文
  factors?: string[]; // 具体的な選択要因（時間適合、車両適合、効率性等）
  assignedTimeSlots?: {
    startTime: string; // HH:mm format - 最初の案件開始時間
    endTime: string; // HH:mm format - 最後の案件終了時間
    totalDuration: number; // 総稼働時間（分）
  };
  workingHours?: {
    start: string; // HH:mm format - 陸送員の勤務開始時間
    end: string; // HH:mm format - 陸送員の勤務終了時間
  };
  // FL-270: 最寄駅情報（Google Maps API統合）
  nearestStation?: string; // 最寄駅名
  nearestStationLat?: number; // 最寄駅緯度
  nearestStationLng?: number; // 最寄駅経度
}

// ヘルプシステム関連の型定義
export interface HelpSection {
  id: string;
  title: string;
  content: string;
  category: HelpCategory;
  roles: Role[]; // この章を表示する対象の役割
  pageContext?: string; // 特定の画面でのみ表示する場合
  order: number; // 表示順序
  searchKeywords: string[]; // 検索用キーワード
}

export interface HelpCategory {
  id: string;
  name: string;
  icon: string; // Heroicon名
  color: string; // Tailwind colorクラス
  order: number;
}

export interface HelpSearchResult {
  section: HelpSection;
  matchScore: number; // 検索スコア（0-1）
}

export interface HelpContextInfo {
  pageId: string;
  pageName: string;
  userRole: Role;
  availableSections: HelpSection[];
}

// FAQ（よくある質問）関連の型定義
export interface HelpFAQ {
  id: string;
  question: string;
  answer: string;
  category: HelpCategory;
  roles: Role[]; // この質問を表示する対象の役割
  pageContext?: string; // 特定の画面でのみ表示する場合
  order: number; // 表示順序
  searchKeywords: string[]; // 検索用キーワード
  tags: string[]; // タグ（フィルタリング用）
}

// 体系的ガイド関連の型定義
export interface HelpGuide {
  id: string;
  title: string;
  description: string;
  sections: HelpGuideSection[];
  category: HelpCategory;
  roles: Role[];
  order: number;
  searchKeywords: string[];
}

export interface HelpGuideSection {
  id: string;
  title: string;
  content: string;
  order: number;
  subsections?: HelpGuideSubsection[];
}

export interface HelpGuideSubsection {
  id: string;
  title: string;
  content: string;
  order: number;
}

// FL-28: 統一取引先マスタ関連の型定義

// FL-87: 取引先データソース（ラクハイ提供/独自）
export enum BusinessPartnerDataSource {
  RAKUHAI_PROVIDED = 'rakuhai_provided', // ラクハイ提供データ
  CUSTOM = 'custom', // 独自登録データ
}

// FL-87: 取引先カテゴリ
export enum BusinessPartnerCategory {
  DEALERSHIP = 'DEALERSHIP', // 売買・買取店
  DEALER = 'DEALER', // ディーラー
  AUCTION = 'AUCTION', // オークション会場
  OTHER = 'OTHER', // その他
}

export enum BusinessPartnerRole {
  BILLING = 'billing', // 請求先
  PICKUP = 'pickup', // 引き取り先
  PICKUP_LOCATION = 'pickup_location', // 引き取り拠点
  DELIVERY = 'delivery', // 搬入先
  DELIVERY_LOCATION = 'delivery_location', // 搬入拠点
  BOTH = 'both', // 両方
}

export enum BusinessPartnerType {
  CORPORATE = 'corporate', // 法人
  INDIVIDUAL = 'individual', // 個人
  DEALER = 'dealer', // ディーラー
  AUCTION = 'auction', // オークション
  WORKSHOP = 'workshop', // 整備工場
  INSURANCE = 'insurance', // 保険会社
  OTHER = 'other', // その他
}

export enum BusinessPartnerStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  SUSPENDED = 'Suspended',
}

export interface BusinessPartnerLocation {
  id: string;
  name: string; // 支店・店舗名（例：「春日部豊町店」）
  address: string;
  phone: string;
  contactPerson: string;
  email?: string;
  isDefault: boolean; // デフォルト拠点かどうか
  roles: BusinessPartnerRole[]; // この拠点が対応する役割
  businessHours?: {
    weekday?: { open: string; close: string };
    saturday?: { open: string; close: string };
    sunday?: { open: string; close: string };
  };
}

export interface BusinessPartner {
  id: string;

  // 基本情報
  name: string; // 企業名または個人名
  displayName?: string; // 表示用名称（省略形など）
  type: BusinessPartnerType;
  status: BusinessPartnerStatus;

  // FL-87: 取引先分類情報
  dataSource: BusinessPartnerDataSource; // データソース（ラクハイ提供/独自）
  category: BusinessPartnerCategory; // カテゴリ（売買店/オークション/その他）

  // 階層構造サポート
  parentId?: string; // 親企業のID（グループ企業の場合）

  // 役割情報
  availableRoles: BusinessPartnerRole[]; // この取引先が対応可能な役割

  // 拠点情報（複数拠点対応）
  locations: BusinessPartnerLocation[];

  // デフォルト情報（後方互換性のため）
  defaultAddress: string;
  defaultPhone: string;
  defaultContactPerson: string;
  defaultEmail: string;

  // メタデータ
  createdAt: string;
  updatedAt: string;
  notes?: string; // 備考

  // SaaS対応
  isSystemPreset: boolean; // システム事前設定データかどうか
  companyId?: string; // 登録した陸送会社ID（独自登録データの場合のみ設定）
}

// 既存のClient型との互換性維持のためのヘルパー型
export interface ClientCompat {
  id: string;
  companyName: string;
  address: string;
  contactPerson: string;
  email: string;
  phone: string;
  status: 'Active' | 'Inactive';
  category?: BusinessPartnerCategory; // FL-87: カテゴリ情報を追加
  type?: BusinessPartnerType; // 事業者種別（法人/個人）
  availableRoles?: BusinessPartnerRole[]; // 対応可能役割
  notes?: string; // 備考
}

// BusinessPartnerからClientCompatへの変換ヘルパー
export function businessPartnerToClientCompat(bp: BusinessPartner): ClientCompat {
  return {
    id: bp.id,
    companyName: bp.name,
    address: bp.defaultAddress,
    contactPerson: bp.defaultContactPerson,
    email: bp.defaultEmail,
    phone: bp.defaultPhone,
    status: bp.status === BusinessPartnerStatus.ACTIVE ? 'Active' : 'Inactive',
    category: bp.category, // FL-87: カテゴリ情報を追加
  };
}

// ClientCompatからBusinessPartnerへの変換ヘルパー
export function clientCompatToBusinessPartner(client: ClientCompat): BusinessPartner {
  return {
    id: client.id,
    name: client.companyName,
    type: client.type || BusinessPartnerType.CORPORATE, // デフォルトは法人
    status:
      client.status === 'Active' ? BusinessPartnerStatus.ACTIVE : BusinessPartnerStatus.INACTIVE,
    // FL-87: カテゴリ情報を追加
    dataSource: BusinessPartnerDataSource.CUSTOM, // Client由来は独自データとする
    category: client.category || BusinessPartnerCategory.OTHER, // デフォルトは「その他」
    availableRoles: client.availableRoles || [
      BusinessPartnerRole.BILLING,
      BusinessPartnerRole.PICKUP,
      BusinessPartnerRole.DELIVERY,
    ], // デフォルトは全役割
    locations: [
      {
        id: `${client.id}-default`,
        name: '本社',
        address: client.address,
        phone: client.phone,
        contactPerson: client.contactPerson,
        email: client.email,
        isDefault: true,
        roles: client.availableRoles || [
          BusinessPartnerRole.BILLING,
          BusinessPartnerRole.PICKUP,
          BusinessPartnerRole.DELIVERY,
        ], // デフォルトは全役割
      },
    ],
    defaultAddress: client.address,
    defaultPhone: client.phone,
    defaultContactPerson: client.contactPerson,
    defaultEmail: client.email,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    notes: client.notes, // 備考を設定
    isSystemPreset: false,
  };
}

// ========================================
// FL-110: 取引先締切ルール管理の型定義
// ========================================

/**
 * 締切ルール種別（搬入/搬出）
 */
export enum DeadlineRuleType {
  PICKUP = 'pickup', // 搬入締切
  DELIVERY = 'delivery', // 搬出締切
}

/**
 * 曜日定数（0=日曜日, 1=月曜日, ..., 6=土曜日）
 * JavaScript標準のDate.getDay()と同じ形式
 */
export enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

/**
 * 取引先締切ルール
 * 各取引先（会場など）の搬入/搬出締切時間を管理
 */
export interface DeadlineRule {
  id: string;
  businessPartnerId: string; // business_partners.id への参照
  companyId: string; // companies.id への参照（マルチテナント対応）
  ruleType: DeadlineRuleType; // 締切種別（搬入/搬出）
  dayOfWeek: DayOfWeek; // 対象曜日（0=日曜日〜6=土曜日）
  deadlineTime: string; // 締切時間（HH:mm形式、例：'14:30'）
  isActive: boolean; // ルール有効フラグ
  notes?: string; // 備考・特記事項
  createdAt: string; // 作成日時
  updatedAt: string; // 更新日時
}

/**
 * 締切ルール作成用の入力データ（IDと日時は自動設定）
 */
export interface CreateDeadlineRuleInput {
  businessPartnerId: string;
  companyId: string;
  ruleType: DeadlineRuleType;
  dayOfWeek: DayOfWeek;
  deadlineTime: string;
  isActive?: boolean; // デフォルト：true
  notes?: string;
}

/**
 * 締切ルール更新用の入力データ（ID以外は任意更新）
 */
export interface UpdateDeadlineRuleInput {
  ruleType?: DeadlineRuleType;
  dayOfWeek?: DayOfWeek;
  deadlineTime?: string;
  isActive?: boolean;
  notes?: string;
}

/**
 * 締切チェック結果
 * 特定の日時に対する締切ルール適用結果
 */
export interface DeadlineCheckResult {
  businessPartnerId: string;
  businessPartnerName: string;
  ruleType: DeadlineRuleType;
  scheduledDateTime: string; // 予定日時
  applicableRule?: DeadlineRule; // 適用される締切ルール（該当する場合）
  deadlineDateTime?: string; // 締切日時（ルールが適用される場合）
  isViolation: boolean; // 締切違反かどうか
  warningMessage?: string; // 警告メッセージ
  dayOfWeekName: string; // 曜日名（表示用）
}

// ========================================
// FL-67: 画像読み込み・OCR関連の型定義
// ========================================

/**
 * 画像OCR処理結果
 */
export interface ImageOCRResult {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  processedAt: string;
  rawText: string;
  confidence: number;
  extractedData: ExtractedAddress[];
  processingTimeMs: number;
  status: OCRProcessingStatus;
  errorMessage?: string;
}

/**
 * OCR処理ステータス
 */
export enum OCRProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * 抽出された住所データ
 */
export interface ExtractedAddress {
  id: string;
  rawText: string;
  postalCode?: string;
  prefecture?: string;
  city?: string;
  town?: string;
  streetAddress?: string;
  buildingName?: string;
  roomNumber?: string;
  fullAddress: string;
  confidence: number;
  extractedAt: string;
  isVerified: boolean;
  validationResult?: AddressValidationResult;
  // FL-67: 自動判定機能用の拡張フィールド
  detectedType?: string;
  detectionConfidence?: number;
  detectionReason?: string;
}

/**
 * 住所検証結果
 */
export interface AddressValidationResult {
  isValid: boolean;
  confidence: number;
  suggestedAddress?: string;
  errorMessage?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * 住所抽出オプション
 */
export interface AddressExtractionOptions {
  includePostalCode: boolean;
  includeBuilding: boolean;
  normalizeFormat: boolean;
  validateWithAPI: boolean;
  confidenceThreshold: number;
}

/**
 * 画像アップロード設定
 */
export interface ImageUploadConfig {
  maxFiles: number;
  maxSizeBytes: number;
  acceptedTypes: string[];
  autoProcess: boolean;
  extractionOptions: AddressExtractionOptions;
}

/**
 * デフォルト画像アップロード設定
 */
export const DEFAULT_IMAGE_UPLOAD_CONFIG: ImageUploadConfig = {
  maxFiles: 5,
  maxSizeBytes: 10 * 1024 * 1024, // 10MB
  acceptedTypes: ['image/jpeg', 'image/png', 'image/heic'],
  autoProcess: true,
  extractionOptions: {
    includePostalCode: true,
    includeBuilding: true,
    normalizeFormat: true,
    validateWithAPI: false, // Phase 1では無効
    confidenceThreshold: 0.6,
  },
};

/**
 * OCR処理結果のイベントハンドラー型
 */
export interface OCREventHandlers {
  onProcessingStart?: (fileName: string) => void;
  onProcessingProgress?: (fileName: string, progress: number) => void;
  onProcessingComplete?: (result: ImageOCRResult) => void;
  onProcessingError?: (fileName: string, error: string) => void;
  onAddressExtracted?: (addresses: ExtractedAddress[]) => void;
}

/**
 * 住所フィールドマッピング（フォーム連携用）
 */
export interface AddressFieldMapping {
  postalCode?: string;
  prefecture?: string;
  city?: string;
  streetAddress?: string;
  fullAddress?: string;
}

// ========================================
// FL-83: アカウント管理機能関連の型定義
// ========================================

export interface PaymentSettings {
  id: string;
  companyId: string;
  stripeCustomerId?: string;
  subscriptionStatus: 'active' | 'inactive' | 'past_due' | 'canceled' | 'trialing';
  subscriptionPlan: string;
  billingEmail: string;
  paymentMethods: PaymentMethod[];
  billingHistory: BillingRecord[];
  createdAt: string;
  updatedAt: string;
}
export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4: string;
  brand?: string; // Visa, Mastercard等
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  createdAt: string;
}

export interface BillingRecord {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  invoiceUrl?: string;
  paidAt?: string;
  createdAt: string;
}

export interface EmailSettings {
  id: string;
  companyId: string;
  primaryEmail: string;
  notificationEmails: string[];
  emailNotifications: {
    scheduleChanges: boolean;
    systemAlerts: boolean;
    billingUpdates: boolean;
    securityNotifications: boolean;
    weeklyReports: boolean;
  };
  emailVerificationStatus: 'verified' | 'pending' | 'failed';
  lastVerificationSent?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeAccount {
  id: string;
  companyId: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'inactive' | 'suspended';
  permissions: EmployeePermission[];
  landTransporterId?: string; // 陸送員アカウントの場合
  lastLoginAt?: string;
  invitationSent: boolean;
  invitationAccepted: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string; // 作成者のユーザーID
}

export interface EmployeePermission {
  resource: string; // 'cases', 'schedule', 'reports', 'master_data', 'settings'
  actions: ('read' | 'write' | 'delete' | 'manage')[]; // 許可されたアクション
}

export interface AccountSettings {
  id: string;
  companyId: string;
  paymentSettings: PaymentSettings;
  emailSettings: EmailSettings;
  employeeAccounts: EmployeeAccount[];
  securitySettings: {
    twoFactorRequired: boolean;
    passwordPolicy: {
      minLength: number;
      requireUppercase: boolean;
      requireNumbers: boolean;
      requireSymbols: boolean;
    };
    sessionTimeout: number; // 分
  };
  createdAt: string;
  updatedAt: string;
}

// ========================================
// マルチテナント対応: 陸送会社・ユーザー管理の型定義
// ========================================

export interface Company {
  id: string;
  name: string;
  code: string;
  address: string;
  phone: string;
  email?: string;
  contactPerson?: string;
  // FL-184: statusカラムは削除され、subscriptionStatusに統合されました
  subscriptionPlan: string;
  subscriptionStatus: string;
  maxUsers: number;
  maxDrivers: number;
  maxVehicles: number;
  settings: Record<string, any>;
  defaultPageSize?: number; // FL-134: ページング設定 - デフォルトページサイズ (5, 10, 20, 50, 100)
  createdAt: string;
  updatedAt: string;
  // FL-114: 陸送員出勤日の公開範囲設定
  driverScheduleVisibility?: DriverScheduleVisibility;
  // FL-115: 陸送員スケジュール編集権限設定
  driverScheduleEditPermission?: DriverScheduleEditPermission;
}

// 取引先マスタ管理で使用する会社情報の簡略版
export interface CompanyInfo {
  id: string;
  name: string;
  code: string;
}

export enum CompanyUserRole {
  ADMIN = 'admin',
  DISPATCHER = 'dispatcher',
  DRIVER = 'driver',
  READ_ONLY = 'read_only',
}

export interface CompanyUser {
  id: string;
  companyId: string;
  email: string;
  name: string;
  role: CompanyUserRole;
  status: 'active' | 'inactive' | 'suspended';
  landTransporterId?: string;
  lastLoginAt?: string;
  invitationSent: boolean;
  invitationAccepted: boolean;
  authUserId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface CompanySubscription {
  id: string;
  companyId: string;
  planType: string;
  status: 'active' | 'inactive' | 'past_due' | 'cancelled' | 'trialing';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  billingEmail?: string;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// マルチテナント対応: 取引先選択・案件管理の型定義
// ========================================

export interface CompanyPartnerSelection {
  id: string;
  companyId: string;
  businessPartnerId: string;
  isEnabled: boolean;
  roles: BusinessPartnerRole[];
  priority: number;
  customName?: string;
  customNotes?: string;
  billingAllowed: boolean;
  pickupAllowed: boolean;
  deliveryAllowed: boolean;
  usageCount: number;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export enum CompanyCaseStatus {
  UNASSIGNED = 'unassigned',
  ASSIGNED = 'assigned',
  IN_TRANSIT = 'in_transit',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface CompanyCase {
  id: string;
  companyId: string;
  caseNumber?: string;
  title?: string;
  status: CompanyCaseStatus;
  priority: number;

  // 取引先情報
  billingPartnerId?: string;
  billingLocationId?: string;
  billingAddressCustom?: string;

  pickupPartnerId?: string;
  pickupLocationId?: string;
  pickupAddressCustom?: string;
  pickupPhone?: string;
  pickupDate: string;
  pickupDateEnd?: string;
  pickupTimeStart?: string;
  pickupTimeEnd?: string;

  deliveryPartnerId?: string;
  deliveryLocationId?: string;
  deliveryAddressCustom?: string;
  deliveryPhone?: string;
  deliveryDate: string;
  deliveryDateEnd?: string;
  deliveryTime?: string;
  deliveryTimeStart?: string;
  deliveryTimeEnd?: string;

  // 車両・陸送情報
  vehiclePlateNumber?: string;
  vehiclePlateNumberNone?: boolean;
  chassisNumber?: string | null; // FL-191: 車体番号（車両ナンバーとは別の識別番号、データベースNULL許容）
  vehicleModel?: string;
  vehicleDisplayName?: string; // company_casesテーブルから取得される車両表示名（例：「トヨタ プリウス」）
  vehicleModelId?: string;
  vehicleNumber?: string;
  assignedLandTransporterId?: string; // DB: assigned_transporter_id（陸送員割り当て）
  transportFee?: number;

  // FL-163: 受付ステータス管理
  acceptanceStatus?: 'provisional' | 'confirmed'; // 仮受（provisional）・本受（confirmed）のステータス

  // FL-113: 車検状態管理
  isVehicleInspectionExpired?: boolean; // 車検切れフラグ
  requiresTemporaryNumber?: boolean; // 仮ナンバー要否
  temporaryNumberNotes?: string; // 仮ナンバー関連備考

  // 依頼者情報
  requester?: string;
  managementNumber?: string;

  // 進捗情報
  pickupReport?: VehicleConditionReport;
  deliveryReport?: VehicleConditionReport;

  // その他
  remarks?: string;
  notes?: string;
  price?: number;

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface CompanyCaseActivity {
  id: string;
  companyId: string;
  caseId: string;
  activityType: string;
  description?: string;
  details: Record<string, any>;
  createdAt: string;
  createdBy?: string;
}

// ========================================
// マルチテナント対応: リソース管理の型定義
// ========================================

export enum CompanyLandTransporterStatus {
  AVAILABLE = 'available',
  BUSY = 'busy',
  UNAVAILABLE = 'unavailable',
  INACTIVE = 'inactive',
}

export interface CompanyLandTransporter {
  id: string;
  companyId: string;
  employeeCode?: string;
  name: string;
  phone: string;
  address?: string;
  status: CompanyLandTransporterStatus;

  // 能力・資格
  drivableVehicleTypes: string[];
  hasLicenseForLargeVehicles: boolean;
  canWorkNightShift: boolean;
  canWorkLongDistance: boolean;
  hasTemporaryLicense: boolean;
  isATOnly: boolean;

  // 勤務時間
  workingDays: Record<string, WorkingTimeSlot | null>;

  // ユーザーアカウント連携
  userId?: string;

  // FL-270: 最寄駅情報（Google Maps統合）
  nearestStation?: string | null; // 最寄駅名 (例: 新宿駅)
  nearestStationPlaceId?: string | null; // Google Maps Place ID
  nearestStationLat?: number | null; // 最寄駅の緯度
  nearestStationLng?: number | null; // 最寄駅の経度

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export enum CompanyVehicleStatus {
  OPERATIONAL = 'operational',
  MAINTENANCE = 'maintenance',
  DECOMMISSIONED = 'decommissioned',
}

export interface CompanyVehicle {
  id: string;
  companyId: string;
  plateNumber: string;
  name?: string;
  vehicleType?: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  status: CompanyVehicleStatus;

  // 仕様
  specifications: Record<string, any>;
  equipment: Record<string, any>;
  restrictions: Record<string, any>;

  // 割当情報
  assignedTransporterId?: string;

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

// ========================================
// マルチテナント対応: 設定管理の型定義
// ========================================

export interface CompanyTimeSettingsNew {
  id: string;
  companyId: string;

  // 引取時間設定
  pickupMinTime: string;
  pickupMaxTime: string;
  pickupExcludedHours: string[];
  pickupMinimumDuration: number;

  // 搬入時間設定
  deliveryMinTime: string;
  deliveryMaxTime: string;
  deliveryExcludedHours: string[];
  deliveryMinimumDuration: number;

  // バッファ時間設定
  betweenCasesBuffer: number;
  lunchStartTime: string;
  lunchEndTime: string;
  lunchBufferMinutes: number;
  localDistanceBuffer: number;
  prefecturalDistanceBuffer: number;
  crossPrefectureBuffer: number;
  dailyPreparationBuffer: number;

  createdAt: string;
  updatedAt: string;
}

export interface CompanyOCRSettingsNew {
  id: string;
  companyId: string;
  ocrEnabled: boolean;
  autoAddressExtraction: boolean;
  autoPhoneExtraction: boolean;
  confidenceThreshold: number;
  maxFilesPerUpload: number;
  maxFileSizeMb: number;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// FL-116: 陸送費設定
// ========================================

export type TransportFeeCalculationMethod = 'fixed' | 'distance_based' | 'vehicle_type_based';

export interface TransportFeeSettings {
  calculation_method: TransportFeeCalculationMethod;
  fixed_fee?: number;
  distance_based_rules?: {
    base_fee: number;
    rules: { from_km: number; to_km: number; fee: number }[];
  };
  vehicle_type_based_fees?: {
    [vehicle_type_id: string]: number;
  };
}

// ========================================
// マルチテナント対応: 履歴・ログの型定義
// ========================================

export interface CompanyPartnerUsageLog {
  id: string;
  companyId: string;
  businessPartnerId: string;
  caseId?: string;
  role: BusinessPartnerRole;
  locationId?: string;
  usedAt: string;
  usedBy?: string;
}

// ========================================
// FL-87: 取引先設定関連の型定義（既存との互換性維持）
// ========================================

export interface CompanyPartnerSettings {
  id: string;
  companyId: string;
  enabledPartnerIds: string[]; // 有効な取引先IDリスト
  dataSourcePreferences: {
    allowRakuhaiProvided: boolean; // ラクハイ提供データの利用可否
    allowCustom: boolean; // 独自データの利用可否
  };
  categoryPreferences: {
    allowDealership: boolean; // 売買・買取店の利用可否
    allowAuction: boolean; // オークション会場の利用可否
    allowOther: boolean; // その他の利用可否
  };
  createdAt: string;
  updatedAt: string;
}

// ========================================
// 車両モデル設定関連の型定義（新機能）
// ========================================

export interface CompanyVehicleModelSettings {
  id: string;
  companyId: string;
  enabledModelIds: string[]; // 有効な車両モデルIDリスト
  dataSourcePreferences: {
    allowRakuhaiProvided: boolean; // ラクハイ提供データの利用可否
    allowCustom: boolean; // 独自データの利用可否
  };
  countryPreferences: {
    allowDomestic: boolean; // 国産車の利用可否
    allowForeign: boolean; // 外国車の利用可否
  };
  powertrainPreferences: {
    allowGasoline: boolean; // ガソリン車の利用可否
    allowDiesel: boolean; // ディーゼル車の利用可否
    allowHybrid: boolean; // ハイブリッド車の利用可否
    allowElectric: boolean; // 電気自動車の利用可否
    allowOther: boolean; // その他パワートレインの利用可否
  };
  tagPreferences: {
    allowImmobilized: boolean; // 不動車の利用可否
    allowLowHeight: boolean; // 低車高車の利用可否
    allowWideBody: boolean; // ワイドボディ車の利用可否
    allowWelfare: boolean; // 福祉車両の利用可否
    allowCamping: boolean; // キャンピングカーの利用可否
    allowClassic: boolean; // クラシックカーの利用可否
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * デフォルト取引先設定
 */
export const DEFAULT_COMPANY_PARTNER_SETTINGS: Omit<
  CompanyPartnerSettings,
  'id' | 'companyId' | 'createdAt' | 'updatedAt'
> = {
  enabledPartnerIds: [],
  dataSourcePreferences: {
    allowRakuhaiProvided: true, // デフォルトでラクハイ提供データを許可
    allowCustom: true, // デフォルトで独自データを許可
  },
  categoryPreferences: {
    allowDealership: true, // デフォルトで売買・買取店を許可
    allowAuction: true, // デフォルトでオークション会場を許可
    allowOther: true, // デフォルトでその他を許可
  },
};

/**
 * デフォルト車両モデル設定
 */
export const DEFAULT_COMPANY_VEHICLE_MODEL_SETTINGS: Omit<
  CompanyVehicleModelSettings,
  'id' | 'companyId' | 'createdAt' | 'updatedAt'
> = {
  enabledModelIds: [],
  dataSourcePreferences: {
    allowRakuhaiProvided: true, // デフォルトでラクハイ提供データを許可
    allowCustom: true, // デフォルトで独自データを許可
  },
  countryPreferences: {
    allowDomestic: true, // デフォルトで国産車を許可（ユーザー要求により選択済み）
    allowForeign: false, // デフォルトで外国車を非選択
  },
  powertrainPreferences: {
    allowGasoline: true, // デフォルトでガソリン車を許可
    allowDiesel: true, // デフォルトでディーゼル車を許可
    allowHybrid: true, // デフォルトでハイブリッド車を許可
    allowElectric: true, // デフォルトで電気自動車を許可
    allowOther: true, // デフォルトでその他パワートレインを許可
  },
  tagPreferences: {
    allowImmobilized: true, // デフォルトで不動車を許可
    allowLowHeight: true, // デフォルトで低車高車を許可
    allowWideBody: true, // デフォルトでワイドボディ車を許可
    allowWelfare: true, // デフォルトで福祉車両を許可
    allowCamping: true, // デフォルトでキャンピングカーを許可
    allowClassic: true, // デフォルトでクラシックカーを許可
  },
};
